import React, { useState, useRef, useEffect } from 'react'
import { Camera, Mic, Square, Terminal, Settings, Brain, Video, Image, Grid, Book, Upload } from 'lucide-react'
import ChatInterface from './components/ChatInterface'
import FileUploadInterface from './components/FileUploadInterface'
import CameraInterface from './components/CameraInterface'
import VoiceInterface from './components/VoiceInterface'
import TerminalInterface from './components/TerminalInterface'
import HealthDashboard from './components/HealthDashboard'
import PersonaSelector from './components/PersonaSelector'
import CollaborativeCanvas from './components/CollaborativeCanvas'
import DocumentationViewer from './components/DocumentationViewer'
import ThemeToggle from './components/ThemeToggle'
import { ThemeProvider } from './contexts/ThemeContext'

type Tab = 'chat' | 'camera' | 'voice' | 'terminal' | 'health' | 'canvas' | 'docs' | 'files'

function App() {
  const [activeTab, setActiveTab] = useState<Tab>('chat')
  const [isCameraActive, setIsCameraActive] = useState(false)
  const [isVoiceActive, setIsVoiceActive] = useState(false)
  const [systemStatus, setSystemStatus] = useState({
    memory: 'healthy',
    qdrant: 'healthy',
    ollama: 'healthy',
    camera: 'available',
    microphone: 'available'
  })

  // Check system status on component mount
  useEffect(() => {
    checkSystemStatus()
    const interval = setInterval(checkSystemStatus, 30000) // Check every 30 seconds
    return () => clearInterval(interval)
  }, [])

  const checkSystemStatus = async () => {
    try {
      const response = await fetch('/api/health')
      if (response.ok) {
        const status = await response.json()
        setSystemStatus(status)
      }
    } catch (error) {
      console.error('Health check failed:', error)
      setSystemStatus(prev => ({
        ...prev,
        memory: 'unhealthy',
        qdrant: 'unhealthy',
        ollama: 'unhealthy'
      }))
    }
  }

  const tabs = [
    { id: 'chat' as Tab, label: 'Chat', icon: Brain, description: 'Text-based conversation' },
    { id: 'camera' as Tab, label: 'Camera', icon: Camera, description: 'Real-time vision processing' },
    { id: 'voice' as Tab, label: 'Voice', icon: Mic, description: 'Voice conversation' },
    { id: 'canvas' as Tab, label: 'Canvas', icon: Grid, description: 'Collaborative drawing with grid coordinates' },
    { id: 'terminal' as Tab, label: 'Terminal', icon: Terminal, description: 'System commands' },
    { id: 'health' as Tab, label: 'Health', icon: Settings, description: 'System status' },
    { id: 'docs' as Tab, label: 'Docs', icon: Book, description: 'Documentation viewer with Mermaid diagrams' },
    { id: 'files' as Tab, label: 'Files', icon: Upload, description: 'File upload and management' },
  ]

  return (
    <ThemeProvider>
      <div className="h-screen flex flex-col bg-gradient-to-br from-[#0f0c29] via-[#302b63] to-[#24243e] dark:from-[#0f0c29] dark:via-[#302b63] dark:to-[#24243e] light:from-[#f8fafc] light:via-[#e2e8f0] light:to-[#cbd5e1] text-white dark:text-white light:text-gray-900">
      {/* Header */}
      <header className="bg-black/20 backdrop-blur-md border-b border-white/10 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-lg">
                <Brain className="w-7 h-7" />
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                  🧙‍♂️ AI WizzarD
                </h1>
                <p className="text-purple-300 text-sm">Enhanced AI Assistant with Voice & Vision</p>
              </div>
            </div>
            
            {/* Persona Selector */}
            <PersonaSelector />
            
            {/* Theme Toggle */}
            <ThemeToggle />
          </div>

          {/* Status Indicators */}
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full animate-pulse ${
                systemStatus.memory === 'healthy' ? 'bg-green-400' : 'bg-red-400'
              }`} />
              <span className="text-sm text-purple-300">Memory</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full animate-pulse ${
                systemStatus.qdrant === 'healthy' ? 'bg-green-400' : 'bg-red-400'
              }`} />
              <span className="text-sm text-purple-300">Qdrant</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full animate-pulse ${
                systemStatus.ollama === 'healthy' ? 'bg-green-400' : 'bg-red-400'
              }`} />
              <span className="text-sm text-purple-300">Ollama</span>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="bg-black/10 backdrop-blur-md border-b border-white/10">
        <div className="flex space-x-1 px-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-3 rounded-t-lg transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-purple-600/30 text-purple-300 border-t-2 border-purple-400'
                  : 'text-purple-300 hover:text-white hover:bg-purple-600/20'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              <span className="font-medium">{tab.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 overflow-hidden">
        {activeTab === 'chat' && (
          <ChatInterface />
        )}
        
        {activeTab === 'camera' && (
          <CameraInterface 
            isActive={isCameraActive}
            onCameraToggle={setIsCameraActive}
          />
        )}
        
        {activeTab === 'voice' && (
          <VoiceInterface 
            isActive={isVoiceActive}
            onVoiceToggle={setIsVoiceActive}
          />
        )}
        
        {activeTab === 'terminal' && (
          <TerminalInterface />
        )}
        
        {activeTab === 'canvas' && (
          <CollaborativeCanvas isActive={true} />
        )}
        
        {activeTab === 'health' && (
          <HealthDashboard status={systemStatus} />
        )}
        
        {activeTab === 'docs' && (
          <DocumentationViewer isActive={true} />
        )}
        
        {activeTab === 'files' && (
          <FileUploadInterface />
        )}
      </main>

      {/* Footer */}
      <footer className="bg-black/20 backdrop-blur-md border-t border-white/10 px-6 py-3">
        <div className="flex items-center justify-between text-sm text-purple-300">
          <div className="flex items-center space-x-4">
            <span>System: {systemStatus.memory === 'healthy' ? 'Operational' : 'Degraded'}</span>
            <span>•</span>
            <span>Memory: 768D Vectors</span>
            <span>•</span>
            <span>Qdrant: Port 6333</span>
          </div>
          <div className="flex items-center space-x-2">
            <Video className="w-4 h-4" />
            <span>Camera: {systemStatus.camera === 'available' ? 'Ready' : 'Unavailable'}</span>
            <span>•</span>
            <Mic className="w-4 h-4" />
            <span>Microphone: {systemStatus.microphone === 'available' ? 'Ready' : 'Unavailable'}</span>
          </div>
        </div>
      </footer>
      </div>
    </ThemeProvider>
  )
}

export default App